/**
 * «JSON 에서 만들기» — 워크스페이스 화면의 버튼 + 숨은 파일 입력 + 확인 창.
 *
 * 파일이 곧 프로젝트다: 프로젝트 JSON(내보내기)을 골라 새 프로젝트를 만든다. 계획(무엇을 가져오고 무엇을 새로
 * 찍는가)은 projectFromJson.planProjectFromFile 이 정하고, 여기는 보여 주고 실행한다. 이름은 파일의 것을 기본값으로
 * 고칠 수 있고, 워크스페이스의 다른 프로젝트와 같은 이름(namePolicy 의 정규화 비교)이면 만들지 않는다 — 마법사와 같은 규칙.
 */
import React, { useRef, useState } from 'react';
import {
  Alert, Button, Chip, DialogActions, DialogContent, DialogContentText, DialogTitle, List, ListItem, ListItemText,
  TextField, Typography,
} from '@mui/material';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { Dialog } from '@/shared/ui/overlays';
import { PROJECT_TITLE_MAX, storedName, validateName } from '@/domain/namePolicy';
import { generateUuid } from '@/shared/uuid';
import { planProjectFromFile, readProjectFileForCreate } from './projectFromJson';
import type { ProjectFromFilePlan } from './projectFromJson';

export interface ImportProjectFromJsonProps {
  /** 계획(이름 반영)으로 프로젝트를 만든다 — 성공하면 새 프로젝트 id */
  onCreate: (plan: ProjectFromFilePlan) => Promise<string | null>;
  onCreated?: (projectId: string) => void;
  /** 중복 차단용 기존 프로젝트 이름 목록 — 정규화 비교(규칙은 namePolicy) */
  existingTitles: readonly string[];
  /** 대상 워크스페이스 표시 이름 */
  workspaceLabel: string;
  disabled?: boolean;
}

const ImportProjectFromJson: React.FC<ImportProjectFromJsonProps> = ({ onCreate, onCreated, existingTitles, workspaceLabel, disabled }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pending, setPending] = useState<{ plan: ProjectFromFilePlan; fileName: string } | null>(null);
  const [name, setName] = useState('');
  const [readError, setReadError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setReadError(null);
    try {
      const read = await readProjectFileForCreate(file);
      if ('error' in read) { setReadError(read.error); return; }
      const plan = planProjectFromFile(read, { now: new Date().toISOString(), newId: generateUuid });
      if ('error' in plan) { setReadError(plan.error); return; }
      setName(plan.name);
      setPending({ plan, fileName: file.name });
    } catch (error) {
      setReadError(`불러오기 실패: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const trimmed = storedName(name);
  // 마법사와 같은 규칙 한 곳(namePolicy): 비어 있지 않고·상한 이내·워크스페이스 안에서 유일
  const nameError = validateName('project', name, existingTitles);

  const handleCreate = async () => {
    if (!pending || nameError) return;
    setCreating(true);
    try {
      const id = await onCreate({ ...pending.plan, name: trimmed });
      if (id) { setPending(null); onCreated?.(id); }
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <input ref={fileInputRef} type="file" accept=".json" style={{ display: 'none' }} onChange={handleFileSelect} data-testid="import-project-file" />
      <Button variant="outlined" startIcon={<FileUploadOutlinedIcon />} onClick={() => fileInputRef.current?.click()} disabled={disabled} sx={{ textTransform: 'none' }}>
        JSON 에서 만들기
      </Button>

      <Dialog open={!!readError} onClose={() => setReadError(null)}>
        <DialogTitle>JSON 에서 만들기</DialogTitle>
        <DialogContent><Alert severity="error">{readError}</Alert></DialogContent>
        <DialogActions><Button onClick={() => setReadError(null)}>닫기</Button></DialogActions>
      </Dialog>

      <Dialog open={!!pending} onClose={() => !creating && setPending(null)}>
        <DialogTitle>JSON 에서 새 프로젝트 만들기</DialogTitle>
        <DialogContent>
          <DialogContentText component="div" sx={{ mb: 2 }}>
            파일 &ldquo;{pending?.fileName}&rdquo; 의 내용으로 <b>{workspaceLabel}</b> 에 새 프로젝트를 만듭니다.
          </DialogContentText>
          <TextField
            label="프로젝트 이름"
            size="small"
            fullWidth
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            inputProps={{ maxLength: PROJECT_TITLE_MAX }}
            error={!!nameError}
            helperText={nameError ?? `${trimmed.length}/${PROJECT_TITLE_MAX}`}
            sx={{ mb: 1 }}
          />
          <Typography variant="subtitle2" sx={{ mt: 1 }}>모델 {pending?.plan.models.length ?? 0}개</Typography>
          <List dense aria-label="가져올 모델">
            {pending?.plan.models.map((m) => (
              <ListItem key={m.name} disableGutters>
                <ListItemText
                  primary={<>{m.name} <Chip size="small" label={m.codes} sx={{ ml: 1 }} /></>}
                  secondary={`${m.nodeCount}개 노드 · 원문 ${m.hasText ? '있음' : '없음'}`}
                />
              </ListItem>
            ))}
          </List>
          <Typography variant="body2" color="text.secondary">
            커플링 설정 {pending?.plan.hasCoSimConfig ? '포함' : '없음'}. 버전 이력·실행 이력·컴파일 결과는 옛 프로젝트의 것이라
            가져오지 않습니다.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPending(null)} disabled={creating}>취소</Button>
          <Button onClick={handleCreate} disabled={creating || !!nameError} variant="contained">만들기</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ImportProjectFromJson;
